import { useGetFeatureFlags } from "@/api/config/config"
import { useGetMoveOutTimeline } from "@/api/move-out-timeline/move-out-timeline"
import { toUKTime } from "@/lib/dateHelper"
import { useGetAccountId } from "@/lib/useGetAccountId"
import { useNow } from "@vueuse/core"
import { computed } from "vue"

import {
  getMoveOutSupportingText,
  getMoveOutTimelineSteps,
  hasUnavailableStep,
  isMoveOutBooked,
  MoveOutTimelineStatus,
  shouldShowMoveOutTimeline,
} from "./moveOutTimelineHelper"

export const useMoveOutTimeline = () => {
  const featureFlagsQuery = useGetFeatureFlags()
  const isFlagEnabled = computed(
    () => !!featureFlagsQuery.data.value?.moveOutTimeline,
  )

  const { accountId } = useGetAccountId()
  const timelineQuery = useGetMoveOutTimeline(accountId, {
    query: {
      enabled: computed(() => isFlagEnabled.value && !!accountId.value),
    },
    // The gateway answers 403 while TMP_SO_33318_MOVE_OUT_TIMELINE is off.
    // That doesn't mean the session has gone, and the card hides on any
    // failure, so don't ask the customer to log in again.
    request: { skipAuthErrorDialog: true },
  })

  // Ticks so the card appears at midnight on the LDOR without a reload.
  const now = useNow({ interval: 60_000 })

  // A failed request leaves us not knowing whether the customer is moving
  // out, so nothing is shown. Errors are only surfaced to customers we know
  // are moving out, when one of their steps could not be resolved.
  const isMovingOut = computed(() =>
    shouldShowMoveOutTimeline(
      isFlagEnabled.value,
      timelineQuery.data.value,
      toUKTime(now.value.toISOString()),
    ),
  )

  const isBooked = computed(() =>
    isMoveOutBooked(isFlagEnabled.value, timelineQuery.data.value),
  )

  const isError = computed(
    () => isMovingOut.value && hasUnavailableStep(timelineQuery.data.value),
  )

  const steps = computed(() =>
    getMoveOutTimelineSteps(timelineQuery.data.value),
  )

  const supportingText = computed(() =>
    getMoveOutSupportingText(timelineQuery.data.value),
  )

  const status = computed(() => timelineQuery.data.value?.status)

  const isClosed = computed(
    () =>
      isMovingOut.value &&
      timelineQuery.data.value?.status === MoveOutTimelineStatus.CLOSED,
  )

  // A disabled query stays pending, so only wait on the timeline when the
  // flag lets it run.
  const isLoading = computed(
    () =>
      featureFlagsQuery.isPending.value ||
      (isFlagEnabled.value && timelineQuery.isPending.value),
  )

  return {
    isMovingOut,
    isBooked,
    isClosed,
    isLoading,
    isError,
    steps,
    supportingText,
    status,
  }
}
